import React, { JSXComponent, useEffect, useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "#next/components/ui/card";
import { Button } from "#next/components/ui/button";
import Link from "next/link";
import { useRouter } from "next/router";
import { useIsUserLoggedIn } from "#next/hooks/use-is-user-logged-in";
import { useApiToken } from "#next/hooks/use-api-token";


export const DashboardBody: JSXComponent = () => {
  const router = useRouter();
  const [ tokenCopied, setTokenCopied ] = useState(false);
  const { isLoggedIn } = useIsUserLoggedIn();
  const [ activeSection, setActiveSection ] = useState("browser-extension");

  // Refs for storing timeouts and observer
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeSectionRef = useRef(activeSection);

  // Update ref when state changes
  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [ activeSection ]);

  // Set up intersection observer to highlight active section
  useEffect(() => {
    // Skip setting up scroll handling if not logged in
    if (isLoggedIn !== true) {
      return;
    }

    const updateActiveNavButton = (sectionId: string) => {
      // Update active state on nav buttons
      document.querySelectorAll("[data-nav]").forEach(btn => {
        btn.classList.remove("ring-2", "ring-indigo-300", "scale-105");
      });
      const currentBtn = document.querySelector(`[data-nav="${sectionId}"]`);
      if (currentBtn) {
        currentBtn.classList.add("ring-2", "ring-indigo-300", "scale-105");
      }
    };

    // Use IntersectionObserver instead of scroll events
    const observerOptions = {
      root: null, // viewport
      rootMargin: "0px",
      threshold: 0.5 // trigger when 50% of element is visible
    };

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      // Sort entries by their position in the viewport
      const visibleEntries = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => {
          const rectA = a.boundingClientRect;
          const rectB = b.boundingClientRect;
          return Math.abs(rectA.top) - Math.abs(rectB.top);
        });

      if (visibleEntries.length > 0) {
        const topEntry = visibleEntries[0];
        const sectionId = topEntry.target.getAttribute("data-section") || "";

        if (sectionId && sectionId !== activeSectionRef.current) {
          setActiveSection(sectionId);
          updateActiveNavButton(sectionId);
        }
      }
    };

    // Create the observer
    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    // Observe all section elements
    const sections = document.querySelectorAll("[data-section]");
    sections.forEach(section => {
      observer.observe(section);
    });

    // Set initial active button
    if (sections.length > 0) {
      const initialSection = sections[0].getAttribute("data-section") || "";
      updateActiveNavButton(initialSection);
    }

    // Cleanup function
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      sections.forEach(section => {
        observer.unobserve(section);
      });
      observer.disconnect();
    };
  }, [ isLoggedIn ]); // Only re-run when login status changes

  const apiToken = useApiToken();

  // Copy token to clipboard
  const copyToClipboard = async () => {
    if (apiToken) {
      await navigator.clipboard.writeText(apiToken);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    }
  };
  return (
    <>
      {/* Main Content - Enhanced with glass morphism and 3D effects */}
      <main className="flex-grow container mx-auto px-6 py-10">
        {/*<div id="hello" className="fixed z-[100000] w-1/4 bg-amber-500 hover:bg-blue-950 test-class">hello</div>*/}
        {/*<div id="hello" className="fixed z-[100000] w-1/4 test-class">hello</div>*/}
        {/* Animated background elements */}
        <div className="fixed inset-0 z-0 overflow-hidden">
          <div className={`absolute -bottom-64 -left-32 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 ${isLoggedIn ? "animate-blob" : ""}`}></div>
          <div className={`absolute -bottom-32 left-48 w-96 h-96 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 ${isLoggedIn ? "animate-blob animation-delay-2000" : ""}`}></div>
          <div className={`absolute top-32 -right-32 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 ${isLoggedIn ? "animate-blob animation-delay-4000" : ""}`}></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 relative z-10">
          {/* Left Sidebar - Navigation with glass morphism */}
          <div className="md:col-span-3 md:sticky md:top-6 md:self-start h-auto space-y-6">
            {/* API Token Display Card - Only shown when logged in */}
            {isLoggedIn && (
              <Card className="bg-white/80 backdrop-blur-md border border-indigo-100/50 shadow-2xl rounded-2xl overflow-hidden transition-all hover:shadow-indigo-200/30 hover:bg-white/90">
                <CardHeader className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 text-white p-3 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSIgZD0iTTAgMGgzMHYzMEgwem0zMCAzMGgzMHYzMEgzMHoiLz48L2c+PC9zdmc+')] opacity-20"></div>
                  <CardTitle className="flex items-center gap-2 text-sm font-bold relative z-10">
                    <div className="p-1.5 bg-white/20 rounded-lg shadow-inner">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
                    </div>
                    Your API Token
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3">
                  <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
                    <div className="relative flex-grow group">
                      <div className="absolute inset-0 bg-gradient-to-r from-indigo-400/20 via-violet-400/20 to-purple-400/20 rounded-lg blur-md transition-opacity opacity-0 group-hover:opacity-100"></div>
                      <input
                        value={apiToken || "No token generated"}
                        readOnly
                        className="w-full font-mono text-sm text-gray-800 border-2 border-indigo-200 bg-white/90 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-300 shadow-md h-10 pl-3 pr-8 rounded-lg relative z-10 transition-all select-all"
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                      {apiToken && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none z-20">
                          <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse shadow-lg shadow-green-200"></div>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      onClick={copyToClipboard}
                      disabled={!apiToken}
                      className="bg-white border-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400 shadow-md hover:shadow-lg font-semibold h-10 min-w-[90px] rounded-lg transition-all duration-300 hover:scale-105 group"
                    >
                      {tokenCopied ? (
                        <span className="flex items-center gap-1 text-sm">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-green-500"><path d="M20 6 9 17l-5-5"/></svg>
                          <span>Copied!</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-sm">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="group-hover:rotate-12 transition-transform duration-300"><rect
                              width="14"
                              height="14"
                              x="8"
                              y="8"
                              rx="2"
                              ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                          <span>Copy</span>
                        </span>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            {!isLoggedIn && (
              <Card className="bg-white/80 backdrop-blur-md border border-indigo-100/50 shadow-2xl rounded-2xl overflow-hidden transition-all hover:shadow-indigo-200/30 hover:bg-white/90">
                <CardHeader className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 text-white p-3 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSIgZD0iTTAgMGgzMHYzMEgwem0zMCAzMGgzMHYzMEgzMHoiLz48L2c+PC9zdmc+')] opacity-20"></div>
                  <CardTitle className="flex items-center gap-2 text-sm font-bold relative z-10">
                    <div className="p-1.5 bg-white/20 rounded-lg shadow-inner">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line
                          x1="15"
                          y1="12"
                          x2="3"
                          y2="12"/></svg>
                    </div>
                    Sign In for API Token
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3">
                  <p className="text-sm text-indigo-700 mb-2">Sign in to access your API token and all features</p>
                  <Link
                    prefetch
                    href="/sign-in"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                      <polyline points="10 17 15 12 10 7"/>
                      <line
                        x1="15"
                        y1="12"
                        x2="3"
                        y2="12"/>
                    </svg>
                    <span>Sign In</span>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Quickstart Steps Card */}
            <Card className="bg-white/80 backdrop-blur-md border border-indigo-100/50 shadow-2xl rounded-2xl overflow-hidden transition-all hover:shadow-indigo-200/30 hover:bg-white/90">
              <CardHeader className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSIgZD0iTTAgMGgzMHYzMEgwem0zMCAzMGgzMHYzMEgzMHoiLz48L2c+PC9zdmc+')] opacity-20"></div>
                <CardTitle className="flex items-center gap-2 text-lg font-bold relative z-10">
                  <div className="p-2 bg-white/20 rounded-lg shadow-inner">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect
                        width="8"
                        height="4"
                        x="8"
                        y="2"
                        rx="1"
                        ry="1"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/></svg>
                  </div>
                  Quickstart Steps
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-5 pb-5 px-4">
                <nav className="flex flex-col space-y-3">
                  {/* Navigation buttons with active state indicator */}
                  {[
                    {
                      id: "browser-extension",
                      label: "Browser Extension",
                      icon: (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"><rect
                            x="2"
                            y="2"
                            width="20"
                            height="8"
                            rx="2"
                            ry="2"/><rect
                            x="2"
                            y="14"
                            width="20"
                            height="8"
                            rx="2"
                            ry="2"/><line
                            x1="6"
                            y1="6"
                            x2="6.01"
                            y2="6"/><line
                            x1="6"
                            y1="18"
                            x2="6.01"
                            y2="18"/></svg>
                      ),
                      color: "from-teal-500 to-cyan-500",
                      hoverBg: "hover:from-teal-500 hover:to-cyan-500"
                    },
                    {
                      id: "mcp-server",
                      label: "MCP Server",
                      icon: (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"><rect
                            width="20"
                            height="12"
                            x="2"
                            y="6"
                            rx="2"/><path d="M12 12h.01"/><path d="M2 12h20"/></svg>
                      ),
                      color: "from-amber-500 to-orange-500",
                      hoverBg: "hover:from-amber-500 hover:to-orange-500"
                    }
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        if ((item as any).href) {
                          router.push((item as any).href);
                          return;
                        }
                        const section = document.querySelector(`[data-section="${item.id}"]`);
                        section?.scrollIntoView({ behavior: "smooth" });

                        // Clear active state from all buttons
                        document.querySelectorAll("[data-nav]").forEach(btn => {
                          btn.classList.remove("ring-2", "ring-indigo-300", "scale-105");
                        });

                        // Set active state on clicked button
                        const currentBtn = document.querySelector(`[data-nav="${item.id}"]`);
                        currentBtn?.classList.add("ring-2", "ring-indigo-300", "scale-105");
                      }}
                      data-nav={item.id}
                      className={`text-left px-4 py-3 font-medium bg-white/90 text-indigo-800 hover:bg-gradient-to-r ${item.hoverBg} hover:text-white rounded-xl shadow-md hover:shadow-lg border border-indigo-100 hover:border-transparent transition-all duration-300 flex items-center gap-3 group hover:scale-105`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-indigo-100 group-hover:bg-white/20 flex items-center justify-center transition-transform duration-300 group-hover:rotate-6">
                        <span className="text-indigo-600 group-hover:text-white transition-all duration-75">{item.icon}</span>
                      </div>
                      <span className="font-semibold text-indigo-800 group-hover:text-white transition-all duration-75">{item.label}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="ml-auto opacity-0 group-hover:opacity-100 transition-all duration-75 transform group-hover:translate-x-1"
                      >
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>

            {/* Resources Section */}
            <Card className="bg-white/80 backdrop-blur-md border border-indigo-100/50 shadow-2xl rounded-2xl overflow-hidden transition-all hover:shadow-indigo-200/30 hover:bg-white/90">
              <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSIgZD0iTTAgMGgzMHYzMEgwem0zMCAzMGgzMHYzMEgzMHoiLz48L2c+PC9zdmc+')] opacity-20"></div>
                <CardTitle className="flex items-center gap-2 text-lg font-bold relative z-10">
                  <div className="p-2 bg-white/20 rounded-lg shadow-inner">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5z"/>
                      <polyline points="10 2 10 14 13 11 16 14 16 2"/>
                    </svg>
                  </div>
                  Resources
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-5 pb-5 px-4">
                <nav className="flex flex-col space-y-3">
                  {/* Resource buttons */}
                  <button
                    onClick={() => {
                      const section = document.querySelector("[data-section=\"claude-code-examples\"]");
                      section?.scrollIntoView({ behavior: "smooth" });

                      // Clear active state from all buttons
                      document.querySelectorAll("[data-nav]").forEach(btn => {
                        btn.classList.remove("ring-2", "ring-indigo-300", "scale-105");
                      });

                      // Set active state on clicked button
                      const currentBtn = document.querySelector("[data-nav=\"claude-code-examples\"]");
                      currentBtn?.classList.add("ring-2", "ring-indigo-300", "scale-105");
                    }}
                    data-nav="claude-code-examples"
                    className="text-left px-4 py-3 font-medium bg-white/90 text-emerald-800 hover:bg-gradient-to-r hover:from-emerald-500 hover:to-green-500 hover:text-white rounded-xl shadow-md hover:shadow-lg border border-emerald-100 hover:border-transparent transition-all duration-300 flex items-center gap-3 group hover:scale-105"
                  >
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 group-hover:bg-white/20 flex items-center justify-center transition-transform duration-300 group-hover:rotate-6">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-emerald-600 group-hover:text-white transition-all duration-75">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                    </div>
                    <span className="font-semibold text-emerald-800 group-hover:text-white transition-all duration-75">Claude Code Examples</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="ml-auto opacity-0 group-hover:opacity-100 transition-all duration-75 transform group-hover:translate-x-1"
                    >
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>

                  <button
                    onClick={() => {
                      const section = document.querySelector("[data-section=\"documentation\"]");
                      section?.scrollIntoView({ behavior: "smooth" });

                      // Clear active state from all buttons
                      document.querySelectorAll("[data-nav]").forEach(btn => {
                        btn.classList.remove("ring-2", "ring-indigo-300", "scale-105");
                      });

                      // Set active state on clicked button
                      const currentBtn = document.querySelector("[data-nav=\"documentation\"]");
                      currentBtn?.classList.add("ring-2", "ring-indigo-300", "scale-105");
                    }}
                    data-nav="documentation"
                    className="text-left px-4 py-3 font-medium bg-white/90 text-teal-800 hover:bg-gradient-to-r hover:from-teal-600 hover:to-emerald-600 hover:text-white rounded-xl shadow-md hover:shadow-lg border border-teal-100 hover:border-transparent transition-all duration-300 flex items-center gap-3 group hover:scale-105"
                  >
                    <div className="w-8 h-8 rounded-lg bg-teal-100 group-hover:bg-white/20 flex items-center justify-center transition-transform duration-300 group-hover:rotate-6">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-teal-600 group-hover:text-white transition-all duration-75"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>
                    </div>
                    <span className="font-semibold text-teal-800 group-hover:text-white transition-all duration-75">Documentation</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="ml-auto opacity-0 group-hover:opacity-100 transition-all duration-75 transform group-hover:translate-x-1"
                    >
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="md:col-span-9 space-y-8">

            {/* Tutorial Video Section */}
            <Card className="border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 shadow-lg overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                <CardTitle className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect
                      x="1"
                      y="5"
                      width="15"
                      height="14"
                      rx="2"
                      ry="2"/></svg>
                  Getting Started: Watch How It Works
                </CardTitle>
                <CardDescription className="text-indigo-100">
                  A quick video introduction to Dom Ghost
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-5 pb-6">
                <div className="rounded-xl overflow-hidden border-2 border-indigo-200 shadow-xl">
                  <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
                    <iframe
                      src="https://www.loom.com/embed/08bc7f02fc1f4aa9b21e895bacb35239?sid=b3aecf26-d79e-4284-a8bf-2a5501d3f68f"
                      // @ts-ignore
                      frameBorder="0"
                      // @ts-ignore
                      webkitallowfullscreen="true"
                      mozallowfullscreen="true"
                      allowFullScreen
                      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                    ></iframe>
                  </div>
                </div>
                <div className="mt-4 bg-gradient-to-r from-indigo-100/80 to-violet-100/80 p-4 rounded-lg border border-indigo-200">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-indigo-600"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>
                      </svg>
                    </div>
                    <p className="text-indigo-800 font-medium">
                      This tutorial video walks you through the complete process of installing the extension, setting up the MCP server, and working with Claude Code to manipulate web pages.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Browser Extension Section */}
            <Card data-section="browser-extension" className="border-2 border-teal-300 bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50 shadow-lg overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-teal-500 to-sky-500 text-white">
                <CardTitle className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"><path d="m6 9 6-6 6 6"/><path d="m6 15 6 6 6-6"/></svg>
                  Step 1: Browser Extension
                </CardTitle>
                <CardDescription className="text-teal-100">
                  Install and configure the Chrome extension
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-5 pb-6">
                <div className="space-y-4">
                  <div className="rounded-md bg-gradient-to-r from-cyan-100/80 to-sky-100/80 p-5 border border-cyan-200 shadow-inner">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-6 w-6 text-cyan-600 animate-pulse"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1 md:flex md:justify-between">
                        <p className="text-cyan-800 font-medium">
                          The browser extension provides DOM access and screenshot capabilities directly from your visited websites.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/*<div className="bg-white/70 rounded-lg p-5 border border-teal-200 shadow">*/}
                    {/*  <h3 className="font-bold text-lg mb-3 text-teal-700 flex items-center gap-2">*/}
                    {/*    <span className="h-7 w-7 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold text-sm">1</span>*/}
                    {/*    Installation Steps*/}
                    {/*  </h3>*/}
                    {/*  <ol className="list-decimal list-outside ml-5 space-y-3 text-teal-800">*/}
                    {/*    <li className="font-medium">Download and install the Dom Ghost extension</li>*/}
                    {/*    <li className="font-medium">Open the extension settings</li>*/}
                    {/*    <li className="font-medium">Enter your API token when prompted</li>*/}
                    {/*    <li className="font-medium">Refresh the pages you want to interact with</li>*/}
                    {/*  </ol>*/}
                    {/*</div>*/}
                    <div className="bg-white/70 rounded-lg p-5 border border-teal-200 shadow">
                      <h3 className="font-bold text-lg mb-3 text-teal-700 flex items-center gap-2">
                        <span className="h-7 w-7 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold text-sm">1</span>
                        Installation Steps
                      </h3>
                      <ol className="list-decimal list-outside ml-5 space-y-3 text-teal-800">
                        <span className="font-medium italic opacity-70 text-gray-700">Until we get verified on the google chrome extensions store, you have to install the extension manually.</span>
                        <li className="font-medium">Click 'Install Extension' Below.</li>
                        <li className="font-medium">Unzip the Extension You Downloaded.</li>
                        <li className="font-medium">Open a New Chrome Tab and Go to chrome://extensions </li>
                        <li className="font-medium">Enable Developer Mode.</li>
                        <li className="font-medium">Click on the Load Unpacked Button.</li>
                        <li className="font-medium">Select the Unzipped Extension Folder.</li>
                        <li className="font-medium">Confirm Extension Addition.</li>
                      </ol>
                    </div>
                    <div className="bg-white/70 rounded-lg p-5 border border-teal-200 shadow">
                      <h3 className="font-bold text-lg mb-3 text-teal-700 flex items-center gap-2">
                        <span className="h-7 w-7 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold text-sm">2</span>
                        Usage Steps
                      </h3>
                      <ol className="list-decimal list-outside ml-5 space-y-3 text-teal-800">
                        <li className="font-medium">Once added to your browser, click on the extension and open it.</li>
                        <li className="font-medium">Enter your API token.</li>
                        <li className="font-medium">Click the 'Use On This Tab' button on the extension when on the page you want to interact with</li>
                      </ol>
                    </div>
                    <div className="bg-white/70 rounded-lg p-5 border border-sky-200 shadow">
                      <h3 className="font-bold text-lg mb-3 text-sky-700 flex items-center gap-2">
                        <span className="h-7 w-7 rounded-full bg-sky-600 flex items-center justify-center text-white font-bold text-sm">✦</span>
                        Features
                      </h3>
                      <ul className="space-y-3 text-sky-800">
                        <li className="font-medium flex items-start gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                          Capture full-page screenshots
                        </li>
                        <li className="font-medium flex items-start gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                          Access the page's DOM structure
                        </li>
                        <li className="font-medium flex items-start gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                          Manipulate DOM elements
                        </li>
                        <li className="font-medium flex items-start gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                          Query element properties
                        </li>
                        <li className="font-medium flex items-start gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                          Simulate user interactions
                        </li>
                        <li className="font-medium flex items-start gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                          Use Workflows for automated tasks
                        </li>
                        <li className="font-medium flex items-start gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                          Work with Claude Code and other AI tools
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="bg-gradient-to-r from-teal-100/50 to-sky-100/50 border-t border-teal-200 pt-6">
                <Button
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = "/dom-ghost-extension-0.1.0.zip";
                    link.download = "dom-ghost-extension-0.1.0.zip";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="bg-gradient-to-r from-teal-500 to-sky-500 hover:from-teal-600 hover:to-sky-600 text-white shadow-md hover:shadow-lg transition-all"
                >
                  <span className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"><path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/><line
                        x1="16"
                        x2="2"
                        y1="8"
                        y2="22"/><line
                        x1="17.5"
                        x2="9"
                        y1="15"/></svg>
                    Install Extension
                  </span>
                </Button>
              </CardFooter>
            </Card>

            {/* MCP Server Setup Section */}
            <Card data-section="mcp-server" className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 shadow-lg overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                <CardTitle className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"><rect
                      width="20"
                      height="12"
                      x="2"
                      y="6"
                      rx="2"/><path d="M12 12h.01"/><path d="M2 12h20"/></svg>
                  Step 2: MCP Server Setup
                </CardTitle>
                <CardDescription className="text-amber-100">
                  Configure your MCP server to access website data
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-5 pb-6">
                <div className="space-y-5">
                  <p className="text-amber-800 font-medium">
                    Run this command to add MCP capabilities to your AI client:
                  </p>
                  <pre className="bg-gradient-to-r from-gray-900 to-gray-800 text-amber-300 p-5 rounded-lg text-sm overflow-x-auto border border-amber-300/30 shadow-md">
                    <code
                      onClick={async () => {
                        await navigator.clipboard.writeText(`npx dom-ghost-mcp ${apiToken || "YOUR_API_TOKEN"}`);
                        // Show temporary tooltip or feedback
                        const tooltip = document.createElement("div");
                        tooltip.textContent = "✓ Copied to clipboard!";
                        tooltip.className = "absolute bg-green-600 text-white px-3 py-1 rounded-md text-xs font-bold";
                        tooltip.style.top = "5px";
                        tooltip.style.right = "5px";
                        const parent = document.activeElement?.parentElement;
                        if (parent) {
                          parent.style.position = "relative";
                          parent.appendChild(tooltip);
                          setTimeout(() => parent.removeChild(tooltip), 2000);
                        }
                      }}
                      className="cursor-pointer hover:text-amber-200 transition-colors font-mono"
                      title="Click to copy to clipboard"
                    >
                      {`npx dom-ghost-mcp ${apiToken || "YOUR_API_TOKEN"}`}
                    </code>
                  </pre>

                  <div className="bg-gradient-to-br from-amber-100/60 to-orange-100/60 p-5 rounded-lg border border-amber-200 shadow-inner">
                    <h4 className="font-bold text-lg mb-3 text-amber-800">How It Works</h4>
                    <p className="text-amber-800 font-medium mb-4">
                      The MCP server connects your AI tools (like Claude) to your browser extension, allowing the AI to:
                    </p>
                    <ul className="space-y-2">
                      <li className="font-medium flex items-center gap-2 text-amber-800">
                        <span className="h-5 w-5 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs">✓</span>
                        Take screenshots of your current page
                      </li>
                      <li className="font-medium flex items-center gap-2 text-amber-800">
                        <span className="h-5 w-5 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs">✓</span>
                        Access and query the DOM structure
                      </li>
                      <li className="font-medium flex items-center gap-2 text-amber-800">
                        <span className="h-5 w-5 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs">✓</span>
                        Manipulate DOM elements
                      </li>
                      <li className="font-medium flex items-center gap-2 text-amber-800">
                        <span className="h-5 w-5 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs">✓</span>
                        Get computed styles for elements
                      </li>
                      <li className="font-medium flex items-center gap-2 text-amber-800">
                        <span className="h-5 w-5 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs">✓</span>
                        View event listeners attached to elements
                      </li>
                      <li className="font-medium flex items-center gap-2 text-amber-800">
                        {/*<span className="h-5 w-5 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs">✓</span>*/}
                        ...And Much More!
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="bg-gradient-to-r from-amber-100/50 to-orange-100/50 border-t border-amber-200 pt-6">
                <div className="flex items-center text-amber-700 font-medium gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                  Click the command to copy it to your clipboard
                </div>
              </CardFooter>
            </Card>


            {/* Claude Code Examples Section */}
            <Card data-section="claude-code-examples" className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-green-50 to-lime-50 shadow-lg overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-emerald-600 to-green-500 text-white">
                <CardTitle className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Step 3: Claude Code Examples
                </CardTitle>
                <CardDescription className="text-emerald-100">
                  See how Claude uses MCP to understand and modify web pages
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-5 pb-6">
                <div className="space-y-6">
                  <div className="bg-white/70 rounded-lg p-5 border border-emerald-200 shadow">
                    <h4 className="italic opacity-50 font-bold text-lg mb-3 text-black flex items-center gap-2">(Your API Key is automatically inserted into the configuration examples below)</h4>
                    <h4 className="font-bold text-lg mb-3 text-emerald-700 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"><rect
                          width="18"
                          height="18"
                          x="3"
                          y="3"
                          rx="2"/><path d="M7 7h.01"/><path d="M17 7h.01"/><path d="M7 17h.01"/><path d="M17 17h.01"/>
                      </svg>
                      Adding DOM Ghost MCP to Claude Code
                    </h4>
                    <h5 className="font-semibold text-emerald-700 mb-3">Using dom-ghost-mcp with Claude Code CLI:</h5>
                    <pre
                      className="bg-gradient-to-r from-emerald-900 to-green-800 text-emerald-200 p-4 rounded-lg text-sm overflow-x-auto border border-emerald-300/30 shadow font-mono mb-4 relative cursor-pointer group"
                      onClick={async () => {
                        let command = `claude mcp add-json dom-ghost-mcp \"{\\"args\\":[\\"dom-ghost-mcp@latest\\",\\"${apiToken ?? "YOUR_API_KEY"}\\"], \\"type\\":\\"stdio\\",\\"command\\":\\"npx\\"}\"`;
                        await navigator.clipboard.writeText(command);
                        // Show temporary tooltip
                        const tooltip = document.createElement("div");
                        tooltip.textContent = "✓ Copied to clipboard!";
                        tooltip.className = "absolute bg-green-600 text-white px-3 py-1 rounded-md text-xs font-bold";
                        tooltip.style.top = "5px";
                        tooltip.style.right = "5px";
                        const parent = document.activeElement;
                        if (parent) {
                          parent.appendChild(tooltip);
                          setTimeout(() => parent.removeChild(tooltip), 2000);
                        }
                      }}
                    >
                      <div className="absolute top-2 right-2 text-emerald-300 opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <rect
                            width="14"
                            height="14"
                            x="8"
                            y="8"
                            rx="2"
                            ry="2" />
                          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                        </svg>
                      </div>
                      <code>{`claude mcp add-json dom-ghost-mcp \"{\\"args\\":[\\"dom-ghost-mcp@latest\\",\\"${apiToken ?? "YOUR_API_KEY"}\\"], \\"type\\":\\"stdio\\",\\"command\\":\\"npx\\"}\"
`}</code>
                    </pre>

                    <h5 className="font-semibold text-emerald-700 mb-3">Using dom-ghost-mcp with a config file (Used by Claude Desktop & Cursor IDE):</h5>
                    <p className="text-emerald-700 mb-2 italic">{"For Claude Desktop, click File > Settings > Developer > Edit Config, and add:"}</p>
                    <pre
                      className="bg-gradient-to-r from-emerald-900 to-green-800 text-emerald-200 p-4 rounded-lg text-sm overflow-x-auto border border-emerald-300/30 shadow font-mono mb-4 relative cursor-pointer group"
                      onClick={async () => {
                        let configJson = `{
  "mcpServers": {
    "dom-ghost-mcp": {
      "command": "npx",
      "args": ["dom-ghost-mcp@latest", "${apiToken || "YOUR_API_KEY"}"]
    }
  }
}`;
                        await navigator.clipboard.writeText(configJson);
                        // Show temporary tooltip
                        const tooltip = document.createElement("div");
                        tooltip.textContent = "✓ Copied to clipboard!";
                        tooltip.className = "absolute bg-green-600 text-white px-3 py-1 rounded-md text-xs font-bold";
                        tooltip.style.top = "5px";
                        tooltip.style.right = "5px";
                        const parent = document.activeElement;
                        if (parent) {
                          parent.appendChild(tooltip);
                          setTimeout(() => parent.removeChild(tooltip), 2000);
                        }
                      }}
                    >
                      <div className="absolute top-2 right-2 text-emerald-300 opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <rect
                            width="14"
                            height="14"
                            x="8"
                            y="8"
                            rx="2"
                            ry="2" />
                          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                        </svg>
                      </div>
                      <code>{`{
  "mcpServers": {
    "dom-ghost-mcp": {
      "command": "npx",
      "args": ["dom-ghost-mcp@latest", "${apiToken ?? "YOUR_API_KEY" }"]
    }
  }
}`}</code>
                    </pre>

                    <h5 className="font-semibold text-emerald-700 mb-3">Using DOM Ghost with an AI Agent:</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white/80 rounded-lg p-4 border border-emerald-200">
                        <h6 className="font-bold text-emerald-700 mb-2">Workflow Approach</h6>
                        <p className="text-emerald-700 mb-2">To start a workflow, start your prompt with <code className="pl-1">use the 'frontend_workflow' tool:</code>, like this:</p>
                        <div
                          className="bg-emerald-50 p-3 rounded text-sm text-emerald-800 font-mono relative cursor-pointer group"
                          onClick={async () => {
                            const prompt = "use the 'start_workflow' tool: Please update my button to do...";
                            await navigator.clipboard.writeText(prompt);
                            // Show temporary tooltip
                            const tooltip = document.createElement("div");
                            tooltip.textContent = "✓ Copied to clipboard!";
                            tooltip.className = "absolute bg-green-600 text-white px-2 py-1 rounded-md text-xs font-bold";
                            tooltip.style.top = "5px";
                            tooltip.style.right = "5px";
                            const parent = document.activeElement;
                            if (parent) {
                              parent.appendChild(tooltip);
                              setTimeout(() => parent.removeChild(tooltip), 2000);
                            }
                          }}
                        >
                          <div className="absolute top-2 right-2 text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round">
                              <rect
                                width="14"
                                height="14"
                                x="8"
                                y="8"
                                rx="2"
                                ry="2" />
                              <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                            </svg>
                          </div>
                          use the 'start_workflow' tool: Please update my button to do...
                        </div>
                      </div>
                      <div className="bg-white/80 rounded-lg p-4 border border-emerald-200">
                        <h6 className="font-bold text-emerald-700 mb-2">Natural Interaction</h6>
                        <p className="text-emerald-700">Simply work with Claude as you normally would and let the agent decide when to use DOM Ghost tools based on your requests.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="bg-gradient-to-r from-emerald-100/50 to-green-100/50 border-t border-emerald-200 pt-6">
                <div className="flex items-center text-emerald-700 font-medium gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                  Claude Code can analyze your website's HTML, CSS, and JavaScript to diagnose issues
                </div>
              </CardFooter>
            </Card>

            {/* Documentation Section */}
            <Card data-section="documentation" className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 via-indigo-50 to-violet-50 shadow-lg overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                <CardTitle className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5z"/><polyline points="10 2 10 14 13 11 16 14 16 2"/></svg>
                  Documentation
                </CardTitle>
                <CardDescription className="text-purple-100">
                  Learn how to use the API and browser extension
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-5 pb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white/70 rounded-lg p-5 border border-purple-200 shadow">
                    <h4 className="font-bold text-lg mb-3 text-purple-700 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <path d="M16 13H8"/>
                        <path d="M16 17H8"/>
                        <path d="M10 9H8"/>
                      </svg>
                      Work In Progress
                      {/*API Documentation*/}
                    </h4>
                    {/*<div className="space-y-4">*/}
                    {/*  <p className="text-purple-800 font-medium">*/}
                    {/*    Our API provides DOM manipulation capabilities through your browser extension.*/}
                    {/*  </p>*/}
                    {/*  <div className="bg-gradient-to-r from-purple-100/80 to-indigo-100/80 p-4 rounded-md border border-purple-200">*/}
                    {/*    <h5 className="font-semibold text-indigo-700 mb-2">Using a DOM Ghost Workflow:</h5>*/}
                    {/*    <p className="text-indigo-800 font-medium mb-4">*/}
                    {/*      To access front-end verification tools:*/}
                    {/*    </p>*/}
                    {/*    <ol className="list-decimal list-inside space-y-2 text-indigo-800 mb-4">*/}
                    {/*      <li>Start your prompt with <span className="font-mono bg-indigo-100 px-1 rounded">use the 'start_workflow' tool:</span> followed by your request</li>*/}
                    {/*      <li>For example: <span className="font-mono bg-indigo-100 px-1 rounded">use the 'start_workflow' tool: Please update my button to do...</span></li>*/}
                    {/*      <li>The workflow will guide you through these steps automatically:*/}
                    {/*        <ul className="list-disc list-inside ml-6 mt-2 text-indigo-700">*/}
                    {/*          <li>Analyze the UI with a CSS selector tree</li>*/}
                    {/*          <li>Capture initial element state</li>*/}
                    {/*          <li>Determine verification approach after changes</li>*/}
                    {/*          <li>Verify changes satisfy requirements</li>*/}
                    {/*        </ul>*/}
                    {/*      </li>*/}
                    {/*    </ol>*/}

                    {/*    <h5 className="font-semibold text-indigo-700 mb-2 mt-6">Basic Commands:</h5>*/}
                    {/*    <ul className="list-disc list-inside space-y-2 text-indigo-800">*/}
                    {/*      <li>*/}
                    {/*        <span className="font-mono bg-indigo-100 px-1 rounded">ui_screenshot</span> - Capture screenshots of DOM elements*/}
                    {/*      </li>*/}
                    {/*      <li>*/}
                    {/*        <span className="font-mono bg-indigo-100 px-1 rounded">element_snapshot</span> - Get detailed element information*/}
                    {/*      </li>*/}
                    {/*      <li>*/}
                    {/*        <span className="font-mono bg-indigo-100 px-1 rounded">css_dom_tree</span> - Generate a CSS selector tree*/}
                    {/*      </li>*/}
                    {/*      <li>*/}
                    {/*        <span className="font-mono bg-indigo-100 px-1 rounded">manipulate</span> - Perform DOM manipulation operations*/}
                    {/*      </li>*/}
                    {/*    </ul>*/}
                    {/*  </div>*/}
                    {/*</div>*/}
                  </div>

                  <div className="bg-white/70 rounded-lg p-5 border border-indigo-200 shadow">
                    <h4 className="font-bold text-lg mb-3 text-indigo-700 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <rect
                          x="2"
                          y="3"
                          width="20"
                          height="14"
                          rx="2"
                          ry="2"/>
                        <line
                          x1="8"
                          y1="21"
                          x2="16"
                          y2="21"/>
                        <line
                          x1="12"
                          y1="17"
                          x2="12"
                          y2="21"/>
                      </svg>
                      Integration Guide
                    </h4>
                    <div className="space-y-4">
                      <p className="text-indigo-800 font-medium">
                        Integrate with popular AI platforms and development tools.
                      </p>
                      <div className="bg-gradient-to-r from-indigo-100/80 to-violet-100/80 p-4 rounded-md border border-indigo-200">
                        <h5 className="font-semibold text-violet-700 mb-2">Compatible With:</h5>
                        <ul className="list-disc list-inside space-y-2 text-violet-800">
                          <li>
                            <span className="font-semibold">Claude 3 (Opus, Sonnet, Haiku)</span>
                          </li>
                          <li>
                            <span className="font-semibold">GPT-4</span> with function calling
                          </li>
                          <li>
                            <span className="font-semibold">Ollama</span> local models
                          </li>
                          <li>
                            <span className="font-semibold">Any MCP compatible</span> AI assistant
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

              </CardContent>
              <CardFooter className="bg-gradient-to-r from-purple-100/50 to-indigo-100/50 border-t border-purple-200 pt-6">
                <a
                  href="/docs"
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5z"/>
                    <polyline points="10 2 10 14 13 11 16 14 16 2"/>
                  </svg>
                  <span>View Full Documentation</span>
                </a>
              </CardFooter>
            </Card>
          </div>
        </div>
      </main>
    </>
  );
};